import { useEffect, useState, useRef } from 'react';
import { isNative } from '@/lib/platform';
import ffaLogo from '@/assets/loading-logo.png';
import splashBg from '@/assets/splash-bg.jpg';

interface SplashProps {
  onComplete: () => void;
}

// Shown once per app launch. Re-mounts (route changes, HMR, resumed webview)
// must not replay the animation — that reads as the app constantly refreshing.
const SPLASH_KEY = 'ffa_splash_shown';
let splashShownThisSession = false;

export default function NativeSplashScreen({ onComplete }: SplashProps) {
  const alreadyShown =
    splashShownThisSession ||
    (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SPLASH_KEY) === '1');

  const [phase, setPhase] = useState<'logo' | 'tagline' | 'done'>(alreadyShown ? 'done' : 'logo');
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!isNative || alreadyShown) {
      // Hide the native splash immediately and let the app render.
      if (isNative) {
        import('@capacitor/splash-screen')
          .then(({ SplashScreen }) => SplashScreen.hide({ fadeOutDuration: 0 }))
          .catch(() => {});
      }
      onCompleteRef.current();
      return;
    }

    splashShownThisSession = true;
    try { sessionStorage.setItem(SPLASH_KEY, '1'); } catch { /* ignore */ }

    // Hide Capacitor's native splash
    import('@capacitor/splash-screen').then(({ SplashScreen }) => {
      SplashScreen.hide({ fadeOutDuration: 0 });
    }).catch(() => {});

    const t1 = setTimeout(() => setPhase('tagline'), 800);
    const t2 = setTimeout(() => setPhase('done'), 1800);
    const t3 = setTimeout(() => onCompleteRef.current(), 2200);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [alreadyShown]);

  if (!isNative || alreadyShown) return null;



  return (
    <>
      {phase !== 'done' && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center">

          {/* Background image */}
          <div className="absolute inset-0">
            <img src={splashBg} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1E3D]/85 via-[#1A3A5C]/70 to-[#2E6B9E]/80" />
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col items-center">
            {/* Logo */}
            <div className="mb-6 animate-[fadeIn_0.6s_ease-out]">
              <img src={ffaLogo} alt="Fantasy Football Africa" className="w-28 h-28 object-contain drop-shadow-2xl" />
            </div>

            {/* App Name */}
            <h1 className="text-white font-bold text-2xl tracking-wide mb-2 animate-[fadeIn_0.4s_ease-out_0.4s_both]">
              Fantasy Football Africa
            </h1>

            {/* Tagline */}
            {phase === 'tagline' && (
              <p className="text-white/90 text-sm tracking-widest uppercase animate-[fadeIn_0.4s_ease-out]">
                Compete. Win. Dominate.
              </p>
            )}
          </div>

          {/* Loading bar */}
          <div className="absolute bottom-16 left-8 right-8 h-0.5 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white/80 rounded-full"
              style={{ animation: 'loadBar 1.8s ease-in-out forwards' }}
            />
          </div>
        </div>
      )}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes loadBar {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </>
  );
}
